import { useEventStore } from '@/state/stores/use-event-store';
import { useModeStore } from '@/state/stores/use-mode-store';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { getObjectId } from '@/helpers/object.id.helper';

// Returns mode based on stored documents:
// - For flows: Uses flowMode store (user can switch) if in flow context
// - For events/sessions: Uses eventMode store (user can switch) if stored, else returns PLAN if PLAN document exists (editable), else ORGANIZE (read-only)
export default function useEventMode(entityId?: string) {
  const flowMode = useModeStore((s) => s.flowMode);
  const eventMode = useModeStore((s) => s.eventMode);
  const eventPlan = useEventStore((s) => s.eventPlan);
  const eventOrganize = useEventStore((s) => s.eventOrganize);
  const event = useEventStore((s) => s.event);
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const flowOrganize = useFlowStore((s) => s.flowOrganize);
  const storeFlow = useFlowStore((s) => s.flow);

  // Check if we're in flow context (indicated by storeFlow and entityId matching)
  const isFlowContext = (() => {
    if (!storeFlow || !entityId) return false;
    const flowId = storeFlow.origin
      ? typeof storeFlow.origin === 'string'
        ? storeFlow.origin
        : storeFlow.origin.id
      : storeFlow.id;
    return flowId === entityId;
  })();

  // For flows in flow context: Check flowMode store first (user can switch modes)
  if (isFlowContext) {
    const flowId = storeFlow!.origin
      ? typeof storeFlow!.origin === 'string'
        ? storeFlow!.origin
        : storeFlow!.origin.id
      : storeFlow!.id;
    if (flowId && flowMode[flowId]) {
      return flowMode[flowId];
    }

    // For flows: Check if PLAN document exists, else ORGANIZE
    // Only apply this logic when explicitly in flow context
    if (flowPlan) {
      return 'PLAN'; // Editable
    }
    if (flowOrganize) {
      return 'ORGANIZE'; // Read-only (no PLAN exists)
    }
    if (storeFlow?.mode) {
      return storeFlow.mode === 'PLAN' ? 'PLAN' : 'ORGANIZE';
    }
  }

  // For events: Check eventMode store first (user can switch modes)
  if (event) {
    const eventOriginId = event.origin ? getObjectId(event.origin) : event.id;
    if (eventOriginId && eventMode[eventOriginId]) {
      return eventMode[eventOriginId];
    }
  }

  // For events: Check if PLAN document exists, else ORGANIZE
  if (eventPlan) {
    return 'PLAN'; // Editable
  }
  if (eventOrganize) {
    return 'ORGANIZE'; // Read-only (no PLAN exists)
  }

  // Fallback: check entityId for direct entity access
  if (entityId) {
    // Could check if entityId matches a flow or event, but for now default to PLAN
    return 'PLAN';
  }

  return 'PLAN';
}
